package com.evoapps.evoshift.domain.usecase.checklist

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.ChecklistCheck
import com.evoapps.evoshift.domain.model.ChecklistItem
import com.evoapps.evoshift.domain.model.ChecklistProblem
import com.evoapps.evoshift.domain.model.ChecklistTemplate
import com.evoapps.evoshift.domain.model.Checklists
import com.evoapps.evoshift.domain.model.InvalidChecklistException
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftChecklist
import com.evoapps.evoshift.domain.repository.ChecklistRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOf
import kotlin.time.Instant

/*
 * SH-121: the checklist use cases. Who may call what is the screens' (Gate.CHECKLISTS,
 * Gate.CHECKLIST_EDITING) and the rules' (firestore.rules): managers keep the templates and read
 * every shift's checklist, the shift's person ticks their own.
 */

/**
 * [checklist] with [itemId] ticked ([done]) or unticked, [note] kept with it (trimmed, cut to
 * [Checklists.NOTE_MAX_LENGTH]). Refuses ticking an item that needs a note without one.
 */
fun ShiftChecklist.tick(itemId: String, done: Boolean, note: String?, now: Instant): ShiftChecklist {
    val item = items.firstOrNull { it.id == itemId } ?: return this
    val clean = note?.trim()?.take(Checklists.NOTE_MAX_LENGTH)?.takeIf { it.isNotEmpty() }
    if (done && item.requiresNote && clean == null) throw InvalidChecklistException(setOf(ChecklistProblem.NOTE_REQUIRED))
    val stamp = now.toString()
    val ticked = copy(checks = checks + (itemId to ChecklistCheck(done = done, note = clean, at = stamp.takeIf { done })), updatedAt = stamp)
    return ticked.copy(completedAt = if (ticked.isComplete) completedAt ?: stamp else null)
}

/** Ticks an item on the person's checklist and saves it ([tick]); returns what was saved. */
class TickChecklistItemUseCase(
    private val repository: ChecklistRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(checklist: ShiftChecklist, itemId: String, done: Boolean, note: String?): Result<ShiftChecklist> = runCatching {
        val ticked = checklist.tick(itemId, done, note, now())
        repository.save(ticked).getOrThrow()
        ticked
    }
}

/**
 * Saves a checklist template: names trimmed, empty trailing rows dropped, new items numbered
 * (`i1`, `i2`, …: ids stay as they are so a checklist ticked before an edit keeps its marks).
 * Refuses one without a name, items or a shift or day to go on ([InvalidChecklistException]).
 */
class SaveChecklistTemplateUseCase(
    private val repository: ChecklistRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(template: ChecklistTemplate, editorId: String): Result<String> = runCatching {
        val clean = normalised(template).copy(updatedAt = now().toString(), updatedBy = editorId)
        val problems = Checklists.problems(clean)
        if (problems.isNotEmpty()) throw InvalidChecklistException(problems)
        repository.saveTemplate(clean).getOrThrow()
    }

    companion object {
        fun normalised(template: ChecklistTemplate): ChecklistTemplate {
            var next = template.items.mapNotNull { it.id.removePrefix("i").toIntOrNull() }.maxOrNull() ?: 0
            val items = template.items
                .map { it.copy(text = it.text.trim()) }
                .dropLastWhile { it.text.isEmpty() }
                .map { item -> if (item.id.isBlank()) item.copy(id = "i${++next}") else item }
            return template.copy(
                name = template.name.trim(),
                items = items,
                shiftTemplateIds = template.shiftTemplateIds.distinct(),
                weekdays = template.weekdays.filter { it in Checklists.WEEKDAYS }.distinct().sorted()
            )
        }
    }
}

class DeleteChecklistTemplateUseCase(private val repository: ChecklistRepository) {
    suspend operator fun invoke(workplaceId: String, templateId: String): Result<Unit> =
        repository.deleteTemplate(workplaceId, templateId)
}

class ObserveChecklistTemplatesUseCase(private val repository: ChecklistRepository) {
    operator fun invoke(workplaceId: String): Flow<List<ChecklistTemplate>> = repository.observeTemplates(workplaceId)
}

/** The checklists of [templates] that go on [shift] ([Checklists.forShift]), the stored ones as ticked. */
internal fun checklistsOf(
    shift: Shift,
    dayShifts: List<Shift>,
    templates: List<ChecklistTemplate>,
    shiftTemplates: List<com.evoapps.evoshift.domain.model.ShiftTemplate>,
    stored: List<ShiftChecklist>
): List<ShiftChecklist> {
    val userId = shift.userId ?: return emptyList()
    val mine = stored.filter { it.shiftId == shift.id && it.userId == userId }
    val byTemplate = mine.associateBy { it.templateId }
    val applicable = Checklists.forShift(shift, dayShifts, templates, shiftTemplates)
        .map { byTemplate[it.id] ?: ShiftChecklist.fresh(it, shift, userId) }
    // Ticked before the template changed or went: still part of the shift's record.
    val kept = mine.filter { it.isStarted && applicable.none { a -> a.templateId == it.templateId } }
    return applicable + kept
}

/**
 * The checklists on [shift] for its person [userId] (the person's own screen): the templates that
 * go on it, ticked as stored, following the roster's changes and the person's ticks.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class ObserveShiftChecklistsUseCase(
    private val shiftRepository: ShiftRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val repository: ChecklistRepository
) {
    operator fun invoke(shift: Shift, userId: String): Flow<List<ShiftChecklist>> {
        val workplaceId = RosterIds.workplaceIdOf(shift.rosterId)
        return flow { emit(workplaceRepository.getShiftTemplates(workplaceId).getOrDefault(emptyList())) }
            .flatMapLatest { shiftTemplates ->
                combine(
                    repository.observeTemplates(workplaceId),
                    shiftRepository.observeShiftsForRoster(shift.rosterId),
                    repository.observeForShift(userId, shift.id)
                ) { templates, rosterShifts, stored ->
                    val current = rosterShifts.firstOrNull { it.id == shift.id } ?: shift
                    if (current.userId != userId) stored.filter { it.isStarted }
                    else checklistsOf(current, rosterShifts.filter { it.date == current.date }, templates, shiftTemplates, stored)
                }
            }
            .distinctUntilChanged()
    }
}

/** One person's shift with its checklists on a day (the managers' view). */
data class ShiftChecklists(val shift: Shift, val checklists: List<ShiftChecklist>)

/**
 * Every assigned shift of [workplaceId] on [date] that has checklists, by planned start, with what
 * was ticked (managers: Today at work and the history). Past days use today's templates for what
 * nobody started; started ones keep their own items.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class ObserveDayChecklistsUseCase(
    private val shiftRepository: ShiftRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val repository: ChecklistRepository
) {
    operator fun invoke(workplaceId: String, date: String): Flow<List<ShiftChecklists>> {
        val (year, month) = runCatching { date.take(4).toInt() to date.substring(5, 7).toInt() }.getOrNull()
            ?: return flowOf(emptyList())
        val rosterId = RosterIds.of(workplaceId, year, month)
        return flow { emit(workplaceRepository.getShiftTemplates(workplaceId).getOrDefault(emptyList())) }
            .flatMapLatest { shiftTemplates ->
                combine(
                    repository.observeTemplates(workplaceId),
                    shiftRepository.observeShiftsForRoster(rosterId),
                    repository.observeDay(workplaceId, date)
                ) { templates, rosterShifts, stored ->
                    val day = rosterShifts.filter { it.date == date && it.userId != null }
                    day.sortedWith(compareBy<Shift>({ it.plannedTime.start }, { it.id }))
                        .map { ShiftChecklists(it, checklistsOf(it, day, templates, shiftTemplates, stored)) }
                        .filter { it.checklists.isNotEmpty() }
                }
            }
            .distinctUntilChanged()
    }
}

/** A day's checklists in short: how many were completed of how many (Today at work). */
data class ChecklistProgress(val done: Int, val total: Int, val items: Int, val itemsDone: Int) {
    val isComplete: Boolean get() = total > 0 && done == total

    companion object {
        fun of(checklists: List<ShiftChecklist>) = ChecklistProgress(
            done = checklists.count { it.isComplete },
            total = checklists.size,
            items = checklists.sumOf { it.items.size },
            itemsDone = checklists.sumOf { it.doneCount }
        )
    }
}

/** Blank item rows for a new template: one to start typing in. */
fun newChecklistTemplate(workplaceId: String) = ChecklistTemplate(
    id = "",
    workplaceId = workplaceId,
    name = "",
    kind = com.evoapps.evoshift.domain.model.ChecklistKind.CLOSING,
    items = listOf(ChecklistItem(id = "", text = ""))
)
